export type ReportType =
  | 'overview'
  | 'financial'
  | 'cashflow'
  | 'receivables'
  | 'appointments'
  | 'productivity'
  | 'patients'
  | 'procedures'
  | 'budgets'
  | 'commissions'
  | 'inventory'
  | 'insurance';

export interface PeriodPreset {
  id: string;
  label: string;
  /** Calcula intervalo `from`/`to` (YYYY-MM-DD). */
  resolve: () => { from: string; to: string };
}

const fmt = (d: Date) => d.toISOString().slice(0, 10);
const today = () => new Date(new Date().toISOString().slice(0, 10) + 'T12:00:00');

export const PERIOD_PRESETS: PeriodPreset[] = [
  {
    id: 'today',
    label: 'Hoje',
    resolve: () => {
      const d = today();
      return { from: fmt(d), to: fmt(d) };
    },
  },
  {
    id: 'this_week',
    label: 'Esta semana',
    resolve: () => {
      const d = today();
      const dayOfWeek = d.getDay() || 7;
      const start = new Date(d);
      start.setDate(d.getDate() - (dayOfWeek - 1));
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'this_month',
    label: 'Este mês',
    resolve: () => {
      const d = today();
      const start = new Date(d.getFullYear(), d.getMonth(), 1);
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'last_month',
    label: 'Mês passado',
    resolve: () => {
      const d = today();
      const start = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const end = new Date(d.getFullYear(), d.getMonth(), 0);
      return { from: fmt(start), to: fmt(end) };
    },
  },
  {
    id: 'last_30_days',
    label: 'Últimos 30 dias',
    resolve: () => {
      const d = today();
      const start = new Date(d);
      start.setDate(d.getDate() - 29);
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'last_90_days',
    label: 'Últimos 90 dias',
    resolve: () => {
      const d = today();
      const start = new Date(d);
      start.setDate(d.getDate() - 89);
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'this_quarter',
    label: 'Este trimestre',
    resolve: () => {
      const d = today();
      const q = Math.floor(d.getMonth() / 3);
      const start = new Date(d.getFullYear(), q * 3, 1);
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'this_year',
    label: 'Este ano',
    resolve: () => {
      const d = today();
      const start = new Date(d.getFullYear(), 0, 1);
      return { from: fmt(start), to: fmt(d) };
    },
  },
  {
    id: 'last_year',
    label: 'Ano passado',
    resolve: () => {
      const d = today();
      const start = new Date(d.getFullYear() - 1, 0, 1);
      const end = new Date(d.getFullYear() - 1, 11, 31);
      return { from: fmt(start), to: fmt(end) };
    },
  },
];

export const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

export const formatNumber = (v: number) =>
  new Intl.NumberFormat('pt-BR').format(Number(v) || 0);

export const formatPct = (v: number | string | null | undefined) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
};

export const formatDate = (s?: string | null) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('pt-BR');
  } catch {
    return s as string;
  }
};

export const formatShortDate = (s?: string | null) => {
  if (!s) return '';
  try {
    const d = new Date(s + 'T12:00:00');
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return s;
  }
};

export interface ReportFiltersState {
  preset: string;
  from: string;
  to: string;
  unitId: string | null;
  unitMode: 'current' | 'all';
  compare: boolean;
  /** Filtro financeiro: apenas lançamentos registrados por este system_users.id (vazio = todos). */
  registrarUserId: string;
}

export const REPORT_TYPE_META: Record<ReportType, { label: string; color: string; description: string }> = {
  overview: { label: 'Visão geral', color: '#0f766e', description: 'Resumo executivo de KPIs do negócio.' },
  financial: { label: 'Financeiro', color: '#22c55e', description: 'Receita, despesas, métodos, categorias e devedores.' },
  cashflow: { label: 'Fluxo de caixa', color: '#0ea5e9', description: 'Entradas, saídas e saldo acumulado por dia.' },
  receivables: { label: 'Contas a receber', color: '#f59e0b', description: 'Aging, próximos vencimentos e devedores.' },
  appointments: { label: 'Agenda', color: '#8b5cf6', description: 'Agendamentos por status, dia, hora e profissional.' },
  productivity: { label: 'Produtividade', color: '#ef4444', description: 'Performance por profissional: consultas e receita.' },
  patients: { label: 'Pacientes', color: '#3b82f6', description: 'Demografia, novos, origens e convênios.' },
  procedures: { label: 'Procedimentos', color: '#14b8a6', description: 'Top procedimentos e conversão orçado→executado.' },
  budgets: { label: 'Orçamentos', color: '#a855f7', description: 'Status, ticket médio e taxa de aprovação.' },
  commissions: { label: 'Comissões', color: '#d946ef', description: 'Comissões por profissional e status.' },
  inventory: { label: 'Estoque', color: '#06b6d4', description: 'Consumo, baixo estoque e movimentações.' },
  insurance: { label: 'Convênios', color: '#f43f5e', description: 'Pacientes e receita por convênio.' },
};
